// Tested in the use-active-circle tests

import {
    CircleCircle,
    CirclePartner,
    CustomAxiosError,
} from '@calm/evexia-web-sdk';
import { MINUTE_MS } from '@calm/web-common/dist/time';
import createCircleApi from '@rhg/libs/api/create-circle-api';
import createPartnerApi from '@rhg/libs/api/create-partner-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { logger } from '@rhg/libs/logger';
import {
    queryKeys,
    queryClient,
    useMutation,
    useQuery,
} from '@rhg/libs/react-query';
import { useCircle } from '../use-circle';

interface CheckPartnerParameters {
    circle: CircleCircle;
    code?: string;
}

export const partnerByCodeQueryOptions = {
    queryKey: (circleId?: number, code?: string) => {
        return [queryKeys.partnerByCode, circleId, code];
    },
    queryFn: async (
        getAccessTokenSilently: () => Promise<string>,
        code: string,
        circleId: number,
        circleAuthorizationToken: string
    ): Promise<CirclePartner | null> => {
        const circleApi = createCircleApi(getAccessTokenSilently);

        const response = await circleApi
            .getPartnersConfigByUniqCode({
                uniqCode: code,
                recipientId: circleId,
                circleAuthorizationToken,
            })
            .catch((e: CustomAxiosError) => {
                if (e?.response?.status === 404) {
                    return { data: null };
                } else {
                    throw e;
                }
            });

        return response?.data;
    },
    queryOptions: (
        isAuthenticated: boolean,
        circleId?: number,
        code?: string,
        circleAuthorizationToken?: string
    ) => {
        return {
            enabled: !!(
                isAuthenticated &&
                circleId &&
                circleId > 0 &&
                code &&
                code?.length > 0 &&
                circleAuthorizationToken
            ),
            retry: false,
            staleTime: 15 * MINUTE_MS,
        };
    },
};

export function usePartnerByCode(circleId: number, code?: string) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT ?? '';
    const { queryKey, queryFn, queryOptions } = partnerByCodeQueryOptions;

    const options = queryOptions(
        isAuthenticated,
        circleId,
        code,
        circleAuthorizationToken
    );

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey(circleId, code),
        queryFn: async () =>
            queryFn(
                getAccessTokenSilently,
                code!,
                circleId,
                circleAuthorizationToken
            ),
        ...options,
    });
    return {
        partner: data,
        isEnabled: options.enabled,
        ...otherProps,
    };
}

export function useCheckPartner() {
    const { getAccessTokenSilently } = useAuth();
    const { queryKey, queryFn } = partnerByCodeQueryOptions;
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({ circle, code }: CheckPartnerParameters) => {
            return queryFn(
                getAccessTokenSilently,
                code!,
                circle.recipient.id,
                circle?.authorizationJWT ?? ''
            );
        },
        onSuccess: (response, { circle, code }) => {
            if (response) {
                queryClient.setQueryData(
                    queryKey(circle.recipient.id, code),
                    response
                );
            }
        },
    });
    return {
        checkPartner: mutate,
        checkPartnerAsync: mutateAsync,
        ...otherProps,
    };
}

export const partnerByIdQueryOptions = {
    queryKey: (partnerId?: string) => {
        return [queryKeys.partnerById, partnerId];
    },
    queryFn: async (
        getAccessTokenSilently: () => Promise<string>,
        partnerId: string
    ): Promise<CirclePartner | null> => {
        const partnerApi = createPartnerApi(getAccessTokenSilently);

        // Diagnostic funnel marker (EVE-12589)
        logger.info('partner.config.request', { partnerId });

        const response = await partnerApi
            .getPartnersConfigById({
                partnerId,
            })
            .catch((e: CustomAxiosError) => {
                if (e?.response?.status === 404) {
                    logger.warn('partner.config.404', { partnerId });
                    return { data: undefined };
                } else {
                    throw e;
                }
            });

        return response?.data ?? null;
    },
    queryOptions: (isAuthenticated: boolean, partnerId?: string) => {
        return {
            enabled: !!(isAuthenticated && partnerId),
            retry: false,
            staleTime: 15 * MINUTE_MS,
        };
    },
};

export function usePartnerById(partnerId?: string) {
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    return usePartnerByIdBase({
        partnerId,
        getAccessTokenSilently,
        isAuthenticated,
    });
}

interface UsePartnerByIdBaseParams {
    partnerId?: string;
    getAccessTokenSilently: () => Promise<string>;
    isAuthenticated?: boolean;
}
export function usePartnerByIdBase({
    partnerId,
    getAccessTokenSilently,
    isAuthenticated = false,
}: UsePartnerByIdBaseParams) {
    const { queryKey, queryFn, queryOptions } = partnerByIdQueryOptions;

    const options = queryOptions(isAuthenticated, partnerId);

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey(partnerId),
        queryFn: async () => queryFn(getAccessTokenSilently, partnerId!),
        ...options,
    });
    return {
        partner: data,
        isEnabled: options.enabled,
        ...otherProps,
    };
}

export function useGetPartnerById() {
    const { getAccessTokenSilently } = useAuth();
    const { queryKey, queryFn } = partnerByIdQueryOptions;
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (partnerId: string) => {
            return queryFn(getAccessTokenSilently, partnerId);
        },
        onSuccess: (response, partnerId) => {
            if (response) {
                queryClient.setQueryData(queryKey(partnerId), response);
            }
        },
    });

    return {
        getPartnerById: mutate,
        getPartnerByIdAsync: mutateAsync,
        ...otherProps,
    };
}
